/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Section, Header } from 'components';
import Content from 'containers/common/Content';
import { Col, Row } from 'react-bootstrap';
import { ToastMessage, ThresholdSettings, AlarmSettings, PuckThresholdSettings, DeveloperMenu, AutoShutoffSettings } from 'containers';
import { UnitDetailHead } from './UnitDetailHead';
import { useParams } from 'react-router-dom';
import {
  Unit, Device, RealTimeDeviceData, Alert, ValveState, ConsumptionPeriod, Location, FormattedConsumptionData, DeviceType, User
} from 'model';
import { DeviceCard, UnitAlertsCard, WaterConsumptionCard, AlertDetailModal } from 'components';
import { useConfirmationPopupFunction, useModalState } from 'effects';
import ConfirmationDialog from 'containers/common/ConfirmationDialog';
import styles from './UnitDetailView.module.css';
import classnames from 'classnames';
import TextConfirmationModal from 'containers/common/TextConfirmationModal';
import { useTranslation } from 'react-i18next';
import RenameDeviceModal from 'containers/common/RenameDeviceModal';

type UnitDetailViewProps = {
  fetchUnitDetails:               (unitId: string) => void,
  currentUnit?:                   Unit,
  currentDevice?:                 Device,
  hasMultipleDevices:             boolean,
  subscribeDeviceData:            (macAddresses: string[]) => void,
  unSubscribeDeviceData:          () => void,
  currentDeviceRealtime?:         RealTimeDeviceData,
  loggedUser?:                    User,
  updatedTelemetryText:           string,
  cleanUnitDetails:               () => void,
  realtimeMap:                    { [macAddress: string]: RealTimeDeviceData },
  setCurrentDevice:               (macAddress: string) => void,
  fetchAlertsByLocation:          (locationId: string) => void,
  isLoadingAlert:                 boolean,
  alertList:                      Alert[],
  isLoadingUnit:                  boolean,
  changeValveState:               (deviceId: string, target: ValveState) => void,
  deleteDevice:                   (deviceId: string) => void,
  updateDevice:                   (deviceId: string, device: Partial<Device>) => void,
  openConfirmationDialog:         (metadata?: { [key: string]: any }) => void,
  openTextConfirmationModal:      (metadata?: { [key: string]: any }) => void,
  openRenameDeviceModal:          (metadata?: { [key: string]: any }) => void,
  currentAlert:                   Alert | null,
  isSubmittingAction:             boolean,
  hasError:                       boolean,
  setCurrentAlert:                (currentAlert: Alert | null) => void,
  resolveAlert:                   (alert: Alert, snoozeSeconds?: number) => void,
  feedbackCriticalAlert:          (alert: Alert, feedbackType: string) => void,
  isSubmittingThresholdSettings:  boolean,
  isSubmittingAlertSettings:      boolean,
  consumptionPeriod:              ConsumptionPeriod,
  fetchConsumptionData:           (period: ConsumptionPeriod, location: Location) => void,
  consumptionData:                FormattedConsumptionData,
  cleanConsumptionData:           () => void,
  fetchAreaTemperatureByLocation: (macAddress: string, timezone: string) => void,
  startPresence:                  (locationId: string) => void,
  stopPresence:                   () => void,
  startTelemetryPolling:          (unitId: string) => void,
  stopTelemetryPolling:           () => void,
  pendingAlertsForCurrentDevice:  Alert[],
  runHealthTest:                  (deviceId: string) => void,
  isSubmittingDeveloperMenu:      boolean,
}

export const UnitDetailView: React.FC<UnitDetailViewProps> = React.memo(({
  fetchUnitDetails,
  currentUnit,
  currentDevice,
  hasMultipleDevices,
  subscribeDeviceData,
  unSubscribeDeviceData,
  currentDeviceRealtime,
  loggedUser,
  updatedTelemetryText,
  cleanUnitDetails,
  realtimeMap,
  setCurrentDevice,
  fetchAlertsByLocation,
  isLoadingAlert,
  alertList,
  isLoadingUnit,
  changeValveState,
  deleteDevice,
  updateDevice,
  openConfirmationDialog,
  openTextConfirmationModal,
  openRenameDeviceModal,
  currentAlert,
  isSubmittingAction,
  hasError,
  setCurrentAlert,
  resolveAlert,
  feedbackCriticalAlert,
  isSubmittingThresholdSettings,
  isSubmittingAlertSettings,
  consumptionPeriod,
  fetchConsumptionData,
  consumptionData,
  cleanConsumptionData,
  fetchAreaTemperatureByLocation,
  startPresence,
  stopPresence,
  startTelemetryPolling,
  stopTelemetryPolling,
  pendingAlertsForCurrentDevice,
  runHealthTest,
  isSubmittingDeveloperMenu,
}) => {
  const { unitId } = useParams();
  const { t } = useTranslation();
  useEffect(() => {
    if (unitId) {
      fetchUnitDetails(unitId);
      fetchAlertsByLocation(unitId);
      startPresence(unitId);
      startTelemetryPolling(unitId);
    }
    return function cleanup() {
      cleanUnitDetails();
      stopPresence();
      stopTelemetryPolling();
    };
  }, [unitId]);

  useEffect(() => {
    if (currentUnit?.id && (currentUnit.devices || []).length > 0) {
      fetchConsumptionData(ConsumptionPeriod.TODAY, currentUnit);
    }
    return function cleanup() {
      cleanConsumptionData();
    }
  }, [currentUnit?.id]);

  useEffect(() => {
    if (currentUnit?.timezone && currentDevice?.macAddress && (currentUnit.devices || []).length > 0) {
      fetchAreaTemperatureByLocation(currentDevice?.macAddress as string, currentUnit.timezone);
    }
  }, [currentUnit?.timezone, currentDevice?.macAddress]);

  const [confirmationData, handleOpenConfirmationPopup] = useConfirmationPopupFunction(openConfirmationDialog);
  const [textConfirmationData, handleOpenTextConfirmationPopup] = useConfirmationPopupFunction(openTextConfirmationModal);
  const [renameDeviceData, handleRenameDevicePopup] = useConfirmationPopupFunction(openRenameDeviceModal);
  const [showDetailModal, setShowDetailModal] = useModalState(isSubmittingAction, hasError);
  const [showThresholdsModal, setShowThresholdsModal] = useModalState(isSubmittingThresholdSettings, hasError);
  const [showAutoShutoffModal, setShowAutoShutoffModal] = useModalState(isSubmittingThresholdSettings, hasError);
  const [showPuckThresholdsModal, setShowPuckThresholdsModal] = useModalState(isSubmittingThresholdSettings, hasError);
  const [showAlarmSettingsModal, setShowAlarmSettingsModal] = useModalState(isSubmittingAlertSettings, hasError);
  const [showDeveloperMenuModal, setShowDeveloperMenuModal] = useModalState(isSubmittingDeveloperMenu, hasError);

  const handleAlertSelect = (alert: Alert) => {
    setCurrentAlert(alert);
    setShowDetailModal(true);
  };

  const waterConsumptionCard = <WaterConsumptionCard
    hasMultipleDevices = {hasMultipleDevices}
    period             = {consumptionPeriod}
    onSelect           = {(period) => (currentUnit?.devices || []).length > 0 ? fetchConsumptionData(period, currentUnit as Location) : {}}
    consumptionData    = {consumptionData}
  />

  return (
    <Section>
      <ToastMessage />
      <Header className={styles.header}>
        <UnitDetailHead unit={currentUnit} />
      </Header>
      <Content>
        <Col
          xs="12"
          className={classnames("pl-0", "pr-0", styles.contentWrapper)}
        >
          <Row>
            <Col xs="12" md="5" className={styles.pr1Desktop}>
              <DeviceCard
                currentDevice={currentDevice}
                currentUnit={currentUnit}
                hasMultipleDevices={hasMultipleDevices}
                currentDeviceRealtime={currentDeviceRealtime}
                loggedUser={loggedUser}
                updatedTelemetryText={updatedTelemetryText}
                deleteDevice={deleteDevice}
                updateDevice={updateDevice}
                realtimeMap={realtimeMap}
                setCurrentDevice={setCurrentDevice}
                isLoadingUnit={isLoadingUnit}
                changeValveState={changeValveState}
                openConfirmationPopup={handleOpenConfirmationPopup}
                openTextConfirmationPopup={handleOpenTextConfirmationPopup}
                handleRenameDevicePopup={handleRenameDevicePopup}
                openSetThresholds={() => {
                  if (currentDevice?.type === DeviceType.DETECTOR) {
                    setShowPuckThresholdsModal(true);
                  } else {
                    setShowThresholdsModal(true);
                  }
                }}
                openAutoShutoff={() => setShowAutoShutoffModal(true)}
                openDeveloperMenu={() => {
                  if (currentDevice?.type === DeviceType.SHUTOFF) {
                    setShowDeveloperMenuModal(true);
                  }
                }}
                openAlarmSettings={() => setShowAlarmSettingsModal(true)}
                pendingAlertsForCurrentDevice={pendingAlertsForCurrentDevice}
                resolveAlert={resolveAlert}
                runHealthTest={runHealthTest}
              />
            </Col>
            <Col xs="12" md="7" className={styles.pl2Desktop}>
              <UnitAlertsCard
                isLoading={isLoadingAlert}
                alerts={alertList}
                locationStats={currentUnit?.notifications?.pending}
                onSelect={handleAlertSelect}
                locationTimezone={currentUnit?.timezone || "US/Pacific"}
              />
              {hasMultipleDevices && waterConsumptionCard}
            </Col>
          </Row>
          {!hasMultipleDevices && currentDevice?.type !== DeviceType.DETECTOR && (
            <Row>
              <Col xs="12">{waterConsumptionCard}</Col>
            </Row>
          )}
          <ConfirmationDialog
            title={confirmationData.title}
            description={confirmationData.description}
            actionText={confirmationData.actionText}
            onAccept={confirmationData.onAccept}
          />
          <TextConfirmationModal
            title={textConfirmationData.title}
            description={textConfirmationData.description}
            actionText={textConfirmationData.actionText}
            onAccept={textConfirmationData.onAccept}
            placeholder={
              currentDevice?.nickname || t("unpair-device-action-text")
            }
            confirmationValidation={(input: string) =>
              input === currentDevice?.nickname ||
              input === t("unpair-device-action-text")
            }
          />
          <RenameDeviceModal
            title={renameDeviceData.title}
            description={renameDeviceData.description}
            actionText={renameDeviceData.actionText}
            onAccept={renameDeviceData.onAccept}
            confirmationValidation={(input: string) => input.length > 0}
          />
          <AlertDetailModal
            show={showDetailModal}
            onHide={() => {
              setShowDetailModal(false);
            }}
            alert={currentAlert}
            onResolve={resolveAlert}
            onFeedbackCriticalAlert={feedbackCriticalAlert}
            isSubmitting={isSubmittingAction}
            location={currentUnit}
          />
          <ThresholdSettings
            show={showThresholdsModal}
            onHide={() => setShowThresholdsModal(false)}
            locations={currentUnit ? [currentUnit] : []}
          />
          <AutoShutoffSettings
            show={showAutoShutoffModal}
            onHide={() => setShowAutoShutoffModal(false)}
            locations={currentUnit ? [currentUnit] : []}
          />
          <PuckThresholdSettings
            show={showPuckThresholdsModal}
            onHide={() => setShowPuckThresholdsModal(false)}
            locations={currentUnit ? [currentUnit] : []}
            device={currentDevice ? currentDevice : ({} as Device)}
          />
          <AlarmSettings
            show={showAlarmSettingsModal}
            onHide={() => setShowAlarmSettingsModal(false)}
            device={currentDevice}
          />
          <DeveloperMenu
            show={showDeveloperMenuModal}
            onHide={() => setShowDeveloperMenuModal(false)}
            locations={currentUnit ? [currentUnit] : []}
            device={currentDevice ? currentDevice : ({} as Device)}
          />
        </Col>
      </Content>
    </Section>
  );
})
